import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CartItem, Product } from '../interfaces/product.interface';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private cartItemsSubject = new BehaviorSubject<CartItem[]>([]);
  public cartItems$ = this.cartItemsSubject.asObservable();

  private cartCountSubject = new BehaviorSubject<number>(0);
  public cartCount$ = this.cartCountSubject.asObservable();

  constructor() {
    this.loadCartFromStorage();
  }

  private loadCartFromStorage(): void {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      const cartItems = JSON.parse(savedCart);
      this.cartItemsSubject.next(cartItems);
      this.updateCartCount();
    }
  }

  private saveCartToStorage(): void {
    localStorage.setItem('cart', JSON.stringify(this.cartItemsSubject.value));
  }

  private updateCartCount(): void {
    const totalCount = this.cartItemsSubject.value.reduce((sum, item) => sum + item.cartQuantity, 0);
    this.cartCountSubject.next(totalCount);
  }

  isItemInCart(productId: string, selectedColor?: string, selectedCategoryId?: string): boolean {
    const currentCart = this.cartItemsSubject.value;
    return currentCart.some(item => 
      item.id === productId && 
      item.selectedColor === selectedColor &&
      item.selectedCategoryId === selectedCategoryId
    );
  }

  addToCart(product: Product, quantity: number = 1, selectedColor?: string, selectedCategoryId?: string): void {
    const currentCart = this.cartItemsSubject.value;
    const existingItemIndex = currentCart.findIndex(
      item => 
        item.id === product.id && 
        item.selectedColor === selectedColor &&
        item.selectedCategoryId === selectedCategoryId
    );

    if (existingItemIndex > -1) {
      currentCart[existingItemIndex].cartQuantity += quantity;
    } else {
      const cartItem: CartItem = {
        ...product,
        cartQuantity: quantity,
        selectedColor,
        selectedCategoryId: (product as any).selectedCategoryId,
        selectedCategoryName: (product as any).selectedCategoryName,
        selectedCategorySubProducts: (product as any).selectedCategorySubProducts,
        selectedSubProductIndex: (product as any).selectedSubProductIndex,
        selectedSubProductText: (product as any).selectedSubProductText,
        // Ensure price and originalPrice are preserved
        price: product.price,
        originalPrice: product.originalPrice
      };
      currentCart.push(cartItem);
    }

    this.cartItemsSubject.next([...currentCart]);
    this.updateCartCount();
    this.saveCartToStorage();
  }

  removeFromCart(productId: string, selectedColor?: string, selectedCategoryId?: string): void {
    const currentCart = this.cartItemsSubject.value;
    const updatedCart = currentCart.filter(
      item => !(
        item.id === productId && 
        item.selectedColor === selectedColor &&
        item.selectedCategoryId === selectedCategoryId
      )
    );
    
    this.cartItemsSubject.next(updatedCart);
    this.updateCartCount();
    this.saveCartToStorage();
  }

  updateQuantity(productId: string, newQuantity: number, selectedColor?: string, selectedCategoryId?: string): void {
    const currentCart = this.cartItemsSubject.value;
    const itemIndex = currentCart.findIndex(
      item => 
        item.id === productId && 
        item.selectedColor === selectedColor &&
        item.selectedCategoryId === selectedCategoryId
    );

    if (itemIndex > -1) {
      if (newQuantity <= 0) {
        this.removeFromCart(productId, selectedColor);
      } else {
        currentCart[itemIndex].cartQuantity = newQuantity;
        this.cartItemsSubject.next([...currentCart]);
        this.updateCartCount();
        this.saveCartToStorage();
      }
    }
  }

  clearCart(): void {
    this.cartItemsSubject.next([]);
    this.cartCountSubject.next(0);
    localStorage.removeItem('cart');
  }

  getCartTotal(): Observable<number> {
    return new BehaviorSubject(
      this.cartItemsSubject.value.reduce((total, item) => total + ((item.price ?? 0) * item.cartQuantity), 0)
    ).asObservable();
  }

  getCartItems(): Observable<CartItem[]> {
    return this.cartItems$;
  }

  getCartCount(): Observable<number> {
    return this.cartCount$;
  }
}
